import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | error
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 8) { setStatus("error"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [refresh]);

  return (
    <Layout showFooter={false}>
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <><Loader2 className="h-12 w-12 text-amber-600 animate-spin mx-auto" />
          <h1 className="font-heading text-2xl font-bold text-[#0A192F] mt-6">Confirming your payment…</h1>
          <p className="text-slate-500 mt-2">This only takes a moment.</p></>
        )}
        {status === "paid" && (
          <><div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
          <h1 className="font-heading text-2xl font-bold text-[#0A192F] mt-6">You're all set! 🎉</h1>
          <p className="text-slate-500 mt-2">Your full access is now active. Everything is ready in your dashboard.</p>
          <Button className="mt-8 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("/dashboard")} data-testid="success-dashboard-btn">Go to Dashboard</Button></>
        )}
        {status === "error" && (
          <><div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mx-auto"><XCircle className="h-8 w-8 text-red-500" /></div>
          <h1 className="font-heading text-2xl font-bold text-[#0A192F] mt-6">We couldn't confirm your payment</h1>
          <p className="text-slate-500 mt-2">If you were charged, your access will unlock shortly. Contact support if it persists.</p>
          <Button variant="outline" className="mt-8" onClick={() => navigate("/upgrade")}>Back to Upgrade</Button></>
        )}
      </div>
    </Layout>
  );
}
